import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { submitOrder } from '@ff-os/pdp-shared-redux/apis/submitOrder';
import { IDT } from '@ff-os/pdp-shared-redux/IDT';
import { selectHasPushError } from '@ff-os/pdp-shared-redux/slices/snapshotSlice';
import { OrderButtonWithPriceLabel } from '@ff-os/shared-react/components/order-button/OrderButtonWithPriceLabel';
import { WindowStackItem } from '@ff-os/shared-react/context/window-stack/WindowStackItem';
import { useMobile } from '@ff-os/shared-react/hooks/useMobile';
import { selectAllPhotos } from '@ff-scene-plugins/photo-prints-redux/slices/photosSlice';
import { PhotoPrintsPdpDispatch, PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { selectTotalRequest } from '../_redux-groups';
import { getPhotosStatus, selectAmountOfUploadsRemaining } from '../_redux-photos';
import { AddingToCartWindow } from './AddingToCartWindow';
import { PhotoPrintsUploadProgress } from './PhotoPrintsUploadProgress';

const _PhotoPrintsOrderButton = ({
    disabled, placeOrder, isUploading,
    className, loading, priceRequest,
    amountOfUploadsRemaining, hasPushError
}: Props) => {

    const { t } = useTranslation();

    const [isOpen, setOpen] = useState(false);
    const [hasError, setError] = useState(false);
    const isMobile = useMobile();

    const placeOrderRef = useRef<Promise<void> | null>(null);

    const tryPlaceOrder = useCallback(() => {
        if (placeOrderRef.current === null) {
            setError(false);
            const promise = placeOrder();
            placeOrderRef.current = promise;
            promise
                .catch(() => setError(true))
                .then(() => placeOrderRef.current = null);
        }
    }, [placeOrder]);

    const onClick = useCallback(() => {
        if (!disabled) {
            setOpen(true);
            if (!isUploading) {
                tryPlaceOrder();
            }
        }
    }, [disabled, isUploading, tryPlaceOrder]);

    useEffect(() => {
        if (isOpen && amountOfUploadsRemaining === 0) {
            tryPlaceOrder();
        }
    }, [amountOfUploadsRemaining]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (hasPushError) {
            setError(true);
        }
    }, [hasPushError]);

    return (
        <>
            <OrderButtonWithPriceLabel
                className={className} fullWidth={isMobile}
                disabled={disabled} loading={loading}
                onClick={onClick} priceRequest={priceRequest}
                pluginName='photo-prints'>

                {t('photo_prints.order_button.text')}

            </OrderButtonWithPriceLabel>

            <WindowStackItem>
                {isOpen ? (
                    <AddingToCartWindow
                        hasError={hasError}
                        dismissError={() => setError(false)}
                        close={() => setOpen(false)}
                        amountOfUploadsRemaining={amountOfUploadsRemaining}
                        isAddingToCart={placeOrderRef.current !== null} />
                ) : (
                    <PhotoPrintsUploadProgress />
                )}
            </WindowStackItem>
        </>
    );
};

type Props = {
    disabled: boolean;
    placeOrder: () => Promise<void>;
    priceRequest: IDT.TotalPriceRequest;
    className?: string;
    loading: boolean;
    isUploading: boolean;
    hasPushError: boolean;
    amountOfUploadsRemaining: number;
};

const mapStateToProps = (state: PhotoPrintsPdpState) => ({
    disabled: selectDisabled(state),
    priceRequest: selectTotalRequest(state),
    isUploading: selectIsUploading(state),
    loading: selectIsPending(state),
    hasPushError: selectHasPushError(state),
    amountOfUploadsRemaining: selectAmountOfUploadsRemaining(state)
});

const mapDispatchToProps = (dispatch: PhotoPrintsPdpDispatch, ownProps: { quantity?: number }) => ({
    placeOrder: () => dispatch(submitOrder(ownProps.quantity))
});

export const PhotoPrintsOrderButton = connect(
    mapStateToProps,
    mapDispatchToProps
)(_PhotoPrintsOrderButton);

const selectIsUploading = createSelector([
    getPhotosStatus
], status => status === 'pending');

const selectHasError = createSelector([
    getPhotosStatus
], (photosStatus) => [photosStatus].includes('error'));

const selectIsPending = createSelector([
    (state: PhotoPrintsPdpState) => state.snapshot.pushStatus,
    (state: PhotoPrintsPdpState) => state.projectLoading.loadStatus,
    (state: PhotoPrintsPdpState) => state.auth.authStatus
], (pushStatus, loadStatus, authStatus) => {
    return [pushStatus, loadStatus, authStatus].includes('pending');
});

const selectDisabled = createSelector([
    selectHasError,
    selectIsPending,
    selectAllPhotos
], (hasError, isPending, photos) => {
    return hasError
        || isPending
        || photos.length === 0;
});